import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Eye, XCircle } from "lucide-react";
import { toast } from "react-toastify";
import { FaEye } from "react-icons/fa";

type Props = {
  show: boolean;
  onClose: () => void;
  onSave?: (contractText: string, logoFile?: File) => Promise<void>;
  initialText?: string;
  initialLogoURL?: string;
};

const ContractCustomization = ({
  show,
  onClose,
  onSave,
  initialText = "",
  initialLogoURL = "",
}: Props) => {
  const [contractText, setContractText] = useState(initialText);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [loadingImage, setLoadingImage] = useState(false);
  const [existingLogoURL, setExistingLogoURL] =
    useState<string>(initialLogoURL);

  const handleLogoChange = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione uma imagem válida");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 2MB");
      return;
    }
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLoadingImage(true);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setExistingLogoURL("");
  };

  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      setLoadingImage(false);
    };
  }, [logoPreview]);

  useEffect(() => {
    if (!show) setShowPreview(false);
  }, [show]);

  useEffect(() => {
    if (show) {
      setContractText(initialText || "");
      setExistingLogoURL(initialLogoURL || "");
      setLogoFile(null);
      if (logoPreview) URL.revokeObjectURL(logoPreview);
      setLogoPreview("");
      if (initialLogoURL) setLoadingImage(true);
    }
  }, [show, initialText, initialLogoURL]);

  useEffect(() => {
    if (logoPreview || existingLogoURL) {
      setLoadingImage(true);
    }
  }, [logoPreview, existingLogoURL]);

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Personalizar Contrato</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Texto do contrato */}
        <div className="mb-3">
          <label className="form-label fw-semibold">
            Regras e cláusulas do contrato
          </label>

          <textarea
            className="form-control"
            rows={6}
            maxLength={1300}
            value={contractText}
            onChange={(e) => setContractText(e.target.value)}
            placeholder="Digite as regras, cláusulas e observações do contrato..."
          />

          <div className="d-flex justify-content-between mt-1">
            <small className="text-muted">Máximo de 1300 caracteres</small>
            <small
              className={
                contractText.length >= 1300 ? "text-danger" : "text-muted"
              }
            >
              {contractText.length}/1300
            </small>
          </div>
        </div>

        {/* Upload da logo */}
        <Form.Group className="mb-3">
          <Form.Label>Logo da empresa (opcional)</Form.Label>

          {!logoPreview && !existingLogoURL ? (
            <div
              className="border border-2 border-dashed rounded p-4 text-center cursor-pointer"
              style={{ cursor: "pointer" }}
              onClick={() => document.getElementById("logoInput")?.click()}
            >
              <p className="mb-1 fw-semibold">Arraste a imagem aqui</p>
              <small className="text-muted">ou clique para selecionar</small>

              <input
                id="logoInput"
                type="file"
                accept="image/png,image/jpeg"
                hidden
                onChange={(e) => handleLogoChange(e.target.files?.[0] || null)}
              />
            </div>
          ) : (
            <div className="card p-3 d-flex align-items-center gap-3 flex-row">
              <div style={{ position: "relative", width: 64, height: 64 }}>
                {loadingImage && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Spinner animation="border" size="sm" />
                  </div>
                )}
                <img
                  src={logoPreview || existingLogoURL}
                  alt="Logo"
                  style={{ width: 64, height: 64, objectFit: "contain" }}
                  className="border rounded"
                  onLoad={() => setLoadingImage(false)}
                  onError={() => setLoadingImage(false)}
                />
              </div>

              <div className="flex-grow-1">
                <strong>
                  {logoFile?.name || (existingLogoURL ? "Logo atual" : "")}
                </strong>
                <div className="text-muted small">
                  {logoFile ? (logoFile.size / 1024).toFixed(1) : ""}
                </div>
              </div>

              {(logoPreview || existingLogoURL) && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                >
                  <FaEye color="white" />
                </Button>
              )}

              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  setLogoFile(null);
                  if (logoPreview) URL.revokeObjectURL(logoPreview);
                  setLogoPreview("");
                  setExistingLogoURL("");
                  setLoadingImage(false);
                }}
              >
                <XCircle size={16} />
              </Button>
            </div>
          )}

          <small className="text-muted d-block mt-1">
            Formatos aceitos: JPG, PNG • Máx: 2MB
          </small>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>

        <Button
          variant="success"
          onClick={async () => {
            if (!onSave) return;
            try {
              setSaving(true);
              await onSave(contractText, logoFile ?? undefined);
            } finally {
              setSaving(false);
            }
          }}
          disabled={saving || !contractText.trim()}
        >
          {saving ? "Salvando..." : "Salvar personalização"}
        </Button>
      </Modal.Footer>
      {showPreview && (
        <Modal show={showPreview} onHide={() => setShowPreview(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Pré-visualização da logo</Modal.Title>
          </Modal.Header>
          <Modal.Body className="d-flex justify-content-center">
            {(logoPreview || existingLogoURL) && (
              <img
                src={logoPreview || existingLogoURL}
                alt="Logo"
                style={{
                  maxWidth: "100%",
                  maxHeight: 300,
                  objectFit: "contain",
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      )}
    </Modal>
  );
};
export default ContractCustomization;
